import { FC, useEffect, useRef, useState } from 'react';
import * as tf from '@tensorflow/tfjs';
import { Box, Button, LinearProgress, MenuItem, TextField, Typography } from '@material-ui/core';
import { Helmet } from 'react-helmet';

import background from '../images/demo/background.jpg';


async function drawMatte(fgr: any, pha: any, canvas: HTMLCanvasElement){
  const rgba = tf.tidy(() => {
      const rgb = (fgr != null) ?
          fgr.squeeze(0).mul(255).cast('int32') :
          tf.fill([pha.shape[1], pha.shape[2], 3], 255, 'int32');
      const a = (pha != null) ?
          pha.squeeze(0).mul(255).cast('int32') :
          tf.fill([fgr.shape[1], fgr.shape[2], 1], 255, 'int32');
      return tf.concat([rgb, a], -1);
  });
  fgr && fgr.dispose();
  pha && pha.dispose();
  const [height, width] = rgba.shape.slice(0, 2);
  const pixelData = new Uint8ClampedArray(await rgba.data());
  const imageData = new ImageData(pixelData, width, height);
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.putImageData(imageData, 0, 0);
  rgba.dispose();
}

async function drawHidden(r: any, canvas: HTMLCanvasElement) {
  const rgba = tf.tidy(() => {
      r = r.unstack(-1)
      r = tf.concat(r, 1)
      r = r.split(4, 1)
      r = tf.concat(r, 2)
      r = r.squeeze(0)
      r = r.expandDims(-1)
      r = r.add(1).mul(127.5).cast('int32')
      r = r.tile([1, 1, 3])
      r = tf.concat([r, tf.fill([r.shape[0], r.shape[1], 1], 255, 'int32')], -1)
      return r;
  });
  const [height, width] = rgba.shape.slice(0, 2);
  const pixelData = new Uint8ClampedArray(await rgba.data());
  const imageData = new ImageData(pixelData, width, height);
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.putImageData(imageData, 0, 0);
  rgba.dispose();
}


// Custom Hooks

const useWebCam = (width: number, height: number) => {
  const stateRef = useRef<any>({webCam: null, stream: null});
  const [state, setState] = useState<any>(stateRef.current);
  useEffect(() => {
    navigator.mediaDevices.getUserMedia({video: {width, height}})
      .then(stream => {
        const video = document.createElement('video');
        video.srcObject = stream;
        tf.data.webcam(video).then(webCam => {
          stateRef.current = {webCam, stream};
          setState(stateRef.current);
        });
      });
    return () => {
      if (stateRef.current.stream) {
        stateRef.current.stream.getTracks().forEach((track: any) => {
          track.stop();
        });
        stateRef.current.webCam.stop();
        stateRef.current = {webCam: null, stream: null};
        setState(stateRef.current);
      }
    }
  }, [width, height]);
  return state;
}

const useModel = (path: string) => {
  const [progress, setProgress] = useState(0);
  const [model, setModel] = useState<tf.GraphModel>();
  useEffect(() => {
    tf.loadGraphModel(path, {onProgress: setProgress}).then(setModel);
  }, [path]);
  return {model, progress};
}


const WebDemoPage: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const selectRef = useRef<HTMLInputElement>(null);
  const refreshRef = useRef(false);
  const animationRef = useRef(-1);

  const {webCam, stream} = useWebCam(640, 480);
  const {model, progress} = useModel(`${process.env.PUBLIC_URL}/model/model.json`);

  useEffect(() => {
    
    let [r1i, r2i, r3i, r4i] = [tf.tensor(0.), tf.tensor(0.), tf.tensor(0.), tf.tensor(0.)];
    const downsample_ratio = tf.tensor(0.5);

    async function loop() {
      if (model && webCam && canvasRef.current && selectRef.current) {
        if (refreshRef.current) {
          tf.dispose([r1i, r2i, r3i, r4i]);
          [r1i, r2i, r3i, r4i] = [tf.tensor(0.), tf.tensor(0.), tf.tensor(0.), tf.tensor(0.)];
          refreshRef.current = false;
        }

        const img = await webCam.capture()
        const src = tf.tidy(() => img.expandDims(0).div(255));
        const out = await model!.executeAsync(
            {src, r1i, r2i, r3i, r4i, downsample_ratio},
            ['fgr', 'pha', 'r1o', 'r2o', 'r3o', 'r4o']);
        const [fgr, pha, r1o, r2o, r3o, r4o] = out as tf.Tensor[];

        if (selectRef.current.value === 'white') {
          drawMatte(fgr.clone(), pha.clone(), canvasRef.current);
          canvasRef.current.style.background = 'none';
        } else if (selectRef.current.value === 'green') {
          drawMatte(fgr.clone(), pha.clone(), canvasRef.current);
          canvasRef.current.style.background = 'rgb(120, 255, 155)';
        } else if (selectRef.current.value === 'image') {
          drawMatte(fgr.clone(), pha.clone(), canvasRef.current);
          const bg = `url("${background}") center center / cover`;
          // A hack around for a browser bug. We need to set the background to none for one frame
          // then set the background to the image.
          if (canvasRef.current.style.background === bg) {
            // Do nothing
          } else if (canvasRef.current.style.background === 'none') {
            canvasRef.current.style.background = bg;
          } else {
            canvasRef.current.style.background = 'none'
          }
        } else if (selectRef.current.value === 'alpha') {
          drawMatte(null, pha.clone(), canvasRef.current);
          canvasRef.current.style.background = '#000';
        } else if (selectRef.current.value === 'foreground') {
          drawMatte(fgr.clone(), null, canvasRef.current);
        } else if (selectRef.current.value === 'recurrent1') {
          drawHidden(r1o, canvasRef.current);
        } else if (selectRef.current.value === 'recurrent2') {
          drawHidden(r2o, canvasRef.current);
        } else if (selectRef.current.value === 'recurrent3') {
          drawHidden(r3o, canvasRef.current);
        } else if (selectRef.current.value === 'recurrent4') {
          drawHidden(r4o, canvasRef.current);
        }

        tf.dispose([img, src, fgr, pha, r1i, r2i, r3i, r4i]);
        [r1i, r2i, r3i, r4i] = [r1o, r2o, r3o, r4o];
      }
      animationRef.current = requestAnimationFrame(loop);
    }

    animationRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animationRef.current);
      tf.dispose([r1i, r2i, r3i, r4i, downsample_ratio]);
    }
  }, [model, webCam]);

  const helmet = () => (
    <Helmet>
      <title>RVM: Web Demo</title>
        <meta name="description" content="Run our model on the web browser." />
    </Helmet>
  );

  const header = () => (
    <Box pt={4} pb={3} textAlign="center">
      <Typography variant="h4" gutterBottom>Robust Video Matting Web Demo</Typography>
      <Typography gutterBottom><strong>Please use Chromium-based browsers!</strong></Typography>
    </Box>
  )

  if (!webCam) {
    return (
      <Box width={1} px={2} py={4} textAlign="center">
        {helmet()}
        {header()}
        <Box py={4}>
          <Typography color="textSecondary">Initializing webcam. Please enable webcam access.</Typography>
        </Box>
      </Box>
    );
  }

  if (progress < 1) {
    return (
      <Box px={2} py={4} textAlign="center">
        {helmet()}
        {header()}
        <Box py={4} maxWidth={512} margin="0 auto">
          <Typography align="center" color="textSecondary" gutterBottom>Downloading the model (4MB)</Typography>
          <LinearProgress variant="determinate" value={progress * 100}/>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={2} py={4} maxWidth={1280} margin="0 auto">
      {helmet()}
      {header()}
      <Box pb={3} textAlign="center">
        <Typography>This demo runs our model in your browser. You can switch to inspect the model's recurrent states.</Typography>
        <Typography>As the human subject moves, our model learns to reconstruct the background in its recurrent states.</Typography>
        <Typography>Even when the camera is moving, the model learns to refresh its memory.</Typography>
      </Box>
      <Box py={2} textAlign="center">
        <TextField defaultValue="white" inputRef={selectRef} label="View" variant="outlined" select>
          <MenuItem value="white">White Background</MenuItem>
          <MenuItem value="green">Green Background</MenuItem>
          <MenuItem value="image">Image Background</MenuItem>
          <MenuItem value="alpha">Alpha</MenuItem>
          <MenuItem value="foreground">Foreground</MenuItem>
          <MenuItem value="recurrent1">Recurrent State 1</MenuItem>
          <MenuItem value="recurrent2">Recurrent State 2</MenuItem>
          <MenuItem value="recurrent3">Recurrent State 3</MenuItem>
          <MenuItem value="recurrent4">Recurrent State 4</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={() => refreshRef.current = true} style={{height: 56, marginLeft: 8}}>
          Refresh Recurrent States
        </Button>
      </Box>
      <Box display="flex">
        <Box width="100%" maxWidth="50%" textAlign="center">
          <Typography variant="caption" color="textSecondary" align="center" paragraph>Input</Typography>
          <video ref={video => { video && (video.srcObject = stream) }} autoPlay style={{maxWidth: "100%"}} />
        </Box>
        <Box width="100%" maxWidth="50%" textAlign="center">
          <Typography variant="caption" color="textSecondary" align="center" paragraph>Output</Typography>
          <canvas ref={canvasRef} style={{maxWidth: "100%"}}/>
        </Box>
      </Box>
      <Box py={5}>
        <Typography align="center" variant="body2" color="textSecondary">The model is running on TensorFlow.js using WebGL.</Typography>
        <Typography align="center" variant="body2" color="textSecondary">The web demo has lowered the quality to trade off for speed. Our official PyTorch model is better and faster.</Typography>
        <Typography align="center" variant="body2" color="textSecondary">If your laptop has two GPUs, consider forcing to use the dedicated GPU in system setting for faster performance.</Typography>
      </Box>
    </Box>
  );
};

export default WebDemoPage;